'use client';

import { useState, FormEvent } from 'react';
import { useRouter } from 'next/navigation';
import { useLocale, useTranslations } from 'next-intl';
import { authModel } from '@/models/authModel';
import {
  AuthForm,
  AuthFormField,
  AuthFormActions,
  AuthFormMessage,
} from '@/components/ui/auth-form';
import { OtpInput } from '@/components/ui';
import { Link } from '@/components/ui';

type Step = 'email' | 'otp';

export default function ForgotPasswordPage() {
  const t = useTranslations('ForgotPassword');
  const locale = useLocale();
  const router = useRouter();

  const [step, setStep] = useState<Step>('email');
  const [email, setEmail] = useState('');
  const [otp, setOtp] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  async function sendOtp(e?: FormEvent<HTMLFormElement>, isResend = false) {
    e?.preventDefault();
    setError(null);
    setSuccess(null);
    setLoading(true);

    try {
      await authModel.sendOtpForPasswordReset(email, locale);
      setSuccess(isResend ? t('otpResent') : t('success'));
      if (!isResend) {
        setStep('otp');
      }
    } catch (err: unknown) {
      const message =
        typeof (err as { message?: string })?.message === 'string'
          ? (err as { message: string }).message
          : t('errorGeneric');
      if (
        message.toLowerCase().includes('rate limit') ||
        message.toLowerCase().includes('too many')
      ) {
        setError('Too many requests. Please wait before requesting another OTP.');
      } else {
        setError(message || t('errorGeneric'));
      }
    } finally {
      setLoading(false);
    }
  }

  async function verifyOtp(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setError(null);
    setSuccess(null);
    setLoading(true);

    try {
      await authModel.verifyOtpForPasswordReset(email, otp);
      // Show success message briefly before redirecting
      setSuccess(t('otpSuccess'));
      // Keep loading state visible and redirect after a short delay
      setTimeout(() => {
        router.push(`/${locale}/reset-password`);
      }, 500);
    } catch (err: unknown) {
      const message =
        typeof (err as { message?: string })?.message === 'string'
          ? (err as { message: string }).message
          : t('otpError');
      if (message.toLowerCase().includes('expired') || message.toLowerCase().includes('invalid')) {
        setError(t('otpExpired'));
      } else {
        setError(message || t('otpError'));
      }
      setLoading(false);
    }
  }

  return (
    <AuthForm
      title={step === 'email' ? t('title') : t('otpStep')}
      subtitle={step === 'email' ? t('subtitle') : t('otpSubtitle')}
      footer={
        <div className="text-center">
          <Link href={`/${locale}/login`} color="primary" size="sm">
            {t('backToLogin')}
          </Link>
        </div>
      }
    >
      {step === 'email' ? (
        <form onSubmit={sendOtp} className="space-y-4">
          <AuthFormField
            label={t('email')}
            type="email"
            name="email"
            value={email}
            onChange={setEmail}
            required
            autoComplete="email"
          />

          {error && <AuthFormMessage type="error" message={error} />}
          {success && <AuthFormMessage type="success" message={success} />}

          <AuthFormActions submitText={loading ? '…' : t('submit')} loading={loading} />
        </form>
      ) : (
        <form onSubmit={verifyOtp} className="space-y-3">
          <div>
            <label className="text-foreground mb-4 block text-sm font-medium">{t('otp')}</label>
            <div className="flex justify-center px-4">
              <OtpInput value={otp} onChange={setOtp} length={6} disabled={loading} />
            </div>
          </div>

          {error && <AuthFormMessage type="error" message={error} />}
          {success && <AuthFormMessage type="success" message={success} />}

          <div className="space-y-2">
            <AuthFormActions
              submitText={loading ? '…' : t('verifyOtp')}
              loading={loading}
              disabled={otp.length !== 6}
            />
            <button
              type="button"
              onClick={() => sendOtp(undefined, true)}
              disabled={loading}
              className="text-muted hover:text-foreground w-full text-sm transition-colors"
            >
              {t('resendOtp')}
            </button>
          </div>
        </form>
      )}
    </AuthForm>
  );
}
